import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Header,
  HeaderProps,
  HeaderTitle,
} from '@rossum/components/header/Header';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconDownload } from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import { SettingsBreadcrumbs } from '../../../containers/Settings/SettingsBreadcrumbs';
import { asScalar } from '../../../lib/url';
import { AddUserDialog } from './add-user/AddUserDialog';

type UsersHeaderProps = {
  activeUsersCount: number;
  search: string;
  setSearch: (search: string) => void;
  onExportUsers: () => void;
  isExporting: boolean;
} & Pick<HeaderProps, 'scrollableDivRef'>;

export const UsersHeader = ({
  activeUsersCount,
  search,
  setSearch,
  onExportUsers,
  isExporting,
  ...rest
}: UsersHeaderProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);

  const intl = useIntl();

  return (
    <>
      <Header
        {...rest}
        breadcrumbs={
          <SettingsBreadcrumbs
            breadcrumbs={[
              {
                label: intl.formatMessage({
                  id: 'containers.settings.users.header.title',
                }),
              },
            ]}
          />
        }
        title={hasScrolled => (
          <Stack direction="row" alignItems="center" gap={1}>
            <HeaderTitle
              hasScrolled={hasScrolled}
              title={intl.formatMessage({
                id: 'containers.settings.users.header.title',
              })}
            />
            <Chip label={activeUsersCount} />
          </Stack>
        )}
        description={intl.formatMessage({
          id: 'containers.settings.users.description',
        })}
        buttons={[
          <SearchInput
            key="search"
            value={asScalar(search) || ''}
            onChange={setSearch}
          />,
          <Tooltip
            key="export"
            title={intl.formatMessage({
              id: 'containers.settings.sidebar.users.exportUsers',
            })}
          >
            <IconButton
              onClick={onExportUsers}
              disabled={isExporting}
              data-cy="users-export-button"
            >
              <SvgIcon fontSize="large">
                <IconDownload />
              </SvgIcon>
            </IconButton>
          </Tooltip>,
          <Button
            key="add"
            variant="contained"
            color="primary"
            onClick={() => setShowDialog(true)}
            data-cy="users-add-user-button"
          >
            {intl.formatMessage({
              id: 'containers.settings.sidebar.users.addUser',
            })}
          </Button>,
        ]}
      />
      <AddUserDialog open={showDialog} onClose={() => setShowDialog(false)} />
    </>
  );
};
